import { useEffect, useRef } from 'react'
import { useGamification } from './useGamification'
import { useClientPreferences } from './useClientPreferences'
import { usePreview } from './usePreview'
import { useToast } from './useToast'
import { useNls } from '../nls/nlsProvider'
import {
  BACKGROUND_COSMETIC_TO_KEY,
  ORBIT_SKIN_COSMETIC_TO_KEY,
  SOUNDSCAPE_COSMETIC_TO_KEY,
  THEME_COSMETIC_TO_KEY,
  type OrbitSkinCosmeticId,
} from '../assets/cosmetics'

/**
 * One-way sync from the gamification profile's equipped cosmetic ids into
 * ClientUserPreferences so the rest of the app (settings UI + CSS) can drive
 * everything from a single source of truth. Reverse direction is handled in
 * place by Settings/CharacterPage which write both at once.
 */
export const CosmeticPreferencesBridge = () => {
  const { profile, equip, isProfileFromCache, pendingThemeId } = useGamification()
  const {
    preferences,
    updatePreferences,
    accountLookMove,
    completeAccountLookMove,
  } = useClientPreferences()
  const { activePreview } = usePreview()
  const { showToast } = useToast()
  const { t } = useNls()

  const preferencesRef = useRef(preferences)
  const accountMoveRanRef = useRef(false)
  const clearedThemeIdRef = useRef<string | null>(null)
  const toastShownRef = useRef(false)

  useEffect(() => {
    preferencesRef.current = preferences
  }, [preferences])

  // Toast once per device when mosaicToastPending
  useEffect(() => {
    if (preferences.mosaicToastPending && !toastShownRef.current) {
      toastShownRef.current = true
      showToast(t('NLSMosaicMovedToast'), 'info')
      updatePreferences({ mosaicToastPending: false })
    }
  }, [preferences.mosaicToastPending, showToast, t, updatePreferences])

  const hasProfile = profile != null

  useEffect(() => {
    if (!profile) return

    const themeKey = THEME_COSMETIC_TO_KEY[profile.equippedThemeId as keyof typeof THEME_COSMETIC_TO_KEY]
    const bgKey = BACKGROUND_COSMETIC_TO_KEY[profile.equippedCanvasBgId]
    const soundKey = SOUNDSCAPE_COSMETIC_TO_KEY[profile.equippedAudioId]
    const orbitKey = ORBIT_SKIN_COSMETIC_TO_KEY[profile.equippedOrbitSkinId as OrbitSkinCosmeticId]
    const currentPreferences = preferencesRef.current

    let isAccountMovePass = false
    if (accountLookMove === 'pending' && !accountMoveRanRef.current) {
      accountMoveRanRef.current = true
      isAccountMovePass = true
      if (themeKey) {
        clearedThemeIdRef.current = profile.equippedThemeId
        void equip({ equippedThemeId: '' })
      }
      void completeAccountLookMove()
    }

    if (clearedThemeIdRef.current !== null && profile.equippedThemeId !== clearedThemeIdRef.current) {
      clearedThemeIdRef.current = null
    }

    const patch: Partial<typeof currentPreferences> = {}
    const isThemeClearedInMove = clearedThemeIdRef.current !== null && profile.equippedThemeId === clearedThemeIdRef.current
    const canPatchTheme =
      accountLookMove === 'done' &&
      !isAccountMovePass &&
      !isThemeClearedInMove &&
      !isProfileFromCache &&
      (pendingThemeId === null || profile.equippedThemeId === pendingThemeId)
    if (canPatchTheme && themeKey && themeKey !== currentPreferences.theme && activePreview?.kind !== 'THEME') {
      patch.theme = themeKey
    }
    if (bgKey && bgKey !== currentPreferences.canvasBackground && activePreview?.kind !== 'BACKGROUND') patch.canvasBackground = bgKey
    if (soundKey && soundKey !== currentPreferences.focusSoundscape && activePreview?.kind !== 'SOUNDSCAPE') patch.focusSoundscape = soundKey
    if (orbitKey && orbitKey !== currentPreferences.orbitSkin && activePreview?.kind !== 'ORBIT') patch.orbitSkin = orbitKey

    if (Object.keys(patch).length > 0) {
      updatePreferences(patch)
      preferencesRef.current = { ...currentPreferences, ...patch }
    }
    // Only replay when equipped cosmetics or preview state change.
    // Manual preference updates should not be overridden by the last equipped cosmetics.
    // A field currently being previewed is skipped so this bridge doesn't stomp the preview mid-countdown.
  }, [
    accountLookMove,
    activePreview?.kind,
    completeAccountLookMove,
    equip,
    hasProfile,
    isProfileFromCache,
    pendingThemeId,
    profile?.equippedAudioId,
    profile?.equippedCanvasBgId,
    profile?.equippedOrbitSkinId,
    profile?.equippedThemeId,
    updatePreferences,
  ])

  return null
}
